import {
  getCompoundFrames,
  projectCompoundGraph,
  type GroupFrame,
} from './compound-projection';
export type { GroupFrame } from './compound-projection';
import { v4 } from 'uuid';
import {
  hasGraphClass,
  toGraphClassList,
  UNAVAILABLE_CLASS,
} from '../lib/classes';
import { computeDominantLinkType } from '../lib/edge-summary';
import {
  GraphFrameSizeDTO,
  GraphViewSnapshotDTO,
  type GraphFrameSize,
  type GraphViewSnapshot,
} from './view-snapshot';
import type {
  GraphCoordinates,
  GraphEdgeDefinition,
  GraphEdgePatch,
  GraphElements,
  GraphNodeDefinition,
  GraphNodePatch,
} from './types';

type Group = GraphViewSnapshot['viewState']['groups'][string];
type Bundle = GraphViewSnapshot['viewState']['edgeBundles'][string];
const clone = <T>(value: T): T => structuredClone(value);
const pair = (source: string, target: string) =>
  JSON.stringify([source, target].sort());

/** Renderer-independent state; Graphology receives only its visible projection. */
export class CompoundGraph {
  nodes = new Map<string, GraphNodeDefinition>();
  edges = new Map<string, GraphEdgeDefinition>();
  groups = new Map<string, Group>();
  bundles = new Map<string, Bundle>();
  private projectionCache?: GraphElements;
  private framesCache?: GroupFrame[];
  private dirty() {
    this.projectionCache = undefined;
    this.framesCache = undefined;
  }
  private children = new Map<string, Set<string>>();
  private adjacent = new Map<string, Set<string>>();
  dragAndDropEnabled = false;

  constructor(elements: GraphElements = {}) {
    this.reset(elements);
  }
  get hasCompounds() {
    return this.groups.size > 0 || this.bundles.size > 0;
  }

  private index() {
    this.dirty();
    this.children.clear();
    this.adjacent.clear();
    for (const node of this.nodes.values()) {
      const parent = node.data.parent;
      if (typeof parent === 'string' && this.nodes.has(parent)) {
        if (!this.children.has(parent)) this.children.set(parent, new Set());
        this.children.get(parent)!.add(node.data.id);
      }
    }
    for (const edge of this.edges.values()) {
      for (const endpoint of [edge.data.source, edge.data.target]) {
        if (!this.adjacent.has(endpoint))
          this.adjacent.set(endpoint, new Set());
        this.adjacent.get(endpoint)!.add(edge.data.id);
      }
    }
  }
  reset(elements: GraphElements) {
    this.nodes = new Map(
      (elements.nodes ?? []).map((node) => [node.data.id, clone(node)]),
    );
    this.edges = new Map(
      (elements.edges ?? []).map((edge) => [edge.data.id, clone(edge)]),
    );
    this.groups.clear();
    this.bundles.clear();
    this.index();
    for (const parent of this.children.keys())
      this.groups.set(parent, {
        kind: this.nodes.get(parent)?.data.ci_type ? 'from-node' : 'wrapper',
        collapsed: false,
        suppressedEdgeIds: [],
      });
  }
  applySnapshot(input: unknown) {
    const snapshot = GraphViewSnapshotDTO.parse(input);
    this.nodes = new Map(
      snapshot.nodes.map((node) => [node.data.id, clone(node)]),
    );
    this.edges = new Map(
      snapshot.edges.map((edge) => [edge.data.id, clone(edge)]),
    );
    this.groups = new Map(Object.entries(snapshot.viewState.groups));
    this.bundles = new Map(Object.entries(snapshot.viewState.edgeBundles));
    this.index();
  }
  snapshot(): GraphViewSnapshot {
    const clean = <T extends GraphNodeDefinition | GraphEdgeDefinition>(
      element: T,
    ) => ({
      ...element,
      classes: toGraphClassList(element.classes).filter(
        (name) =>
          !/^status-[0-6]$/.test(name) && !name.startsWith('bulk-link-'),
      ),
    });
    const persistentEdges = [...this.edges.values()].filter(
      (edge) => !['virtual', 'bulk-link-preview'].includes(edge.data.type),
    );
    const edgeIds = new Set(persistentEdges.map((edge) => edge.data.id));
    return GraphViewSnapshotDTO.parse(
      JSON.parse(
        JSON.stringify({
          version: 1,
          nodes: [...this.nodes.values()].map(clean),
          edges: persistentEdges.map(clean),
          viewState: {
            groups: Object.fromEntries(
              [...this.groups].map(([id, group]) => [
                id,
                {
                  ...group,
                  suppressedEdgeIds: group.suppressedEdgeIds.filter((edge) =>
                    edgeIds.has(edge),
                  ),
                },
              ]),
            ),
            edgeBundles: Object.fromEntries(this.bundles),
          },
        }),
      ),
    );
  }
  syncPositions(nodes: GraphNodeDefinition[]) {
    this.dirty();
    for (const node of nodes) {
      const current = this.nodes.get(node.data.id);
      if (current && node.position) {
        if (this.groups.get(node.data.id)?.collapsed)
          this.translate([node.data.id], {
            x: node.position.x - (current.position?.x ?? 0),
            y: node.position.y - (current.position?.y ?? 0),
          });
        else current.position = { ...node.position };
      }
    }
  }
  descendants(id: string): string[] {
    const result: string[] = [];
    const pending = [...(this.children.get(id) ?? [])];
    const seen = new Set([id]);
    while (pending.length) {
      const next = pending.pop()!;
      if (seen.has(next)) continue;
      seen.add(next);
      result.push(next);
      pending.push(...(this.children.get(next) ?? []));
    }
    return result;
  }
  roots(ids: string[]) {
    const selected = new Set(ids.filter((id) => this.nodes.has(id)));
    return [...selected].filter((id) => {
      let parent = this.nodes.get(id)?.data.parent;
      while (typeof parent === 'string') {
        if (selected.has(parent)) return false;
        parent = this.nodes.get(parent)?.data.parent;
      }
      return true;
    });
  }
  translate(ids: string[], delta: GraphCoordinates) {
    this.dirty();
    const moved = new Set(
      this.roots(ids).flatMap((id) => [id, ...this.descendants(id)]),
    );
    for (const id of moved) {
      const node = this.nodes.get(id)!;
      const position = node.position ?? { x: 0, y: 0 };
      node.position = { x: position.x + delta.x, y: position.y + delta.y };
    }
    return [...moved];
  }
  resizeGroup(id: string, size: GraphFrameSize) {
    this.dirty();
    const group = this.groups.get(id);
    if (group) group.frameSize = GraphFrameSizeDTO.parse(size);
  }
  private refreshSuppression() {
    this.dirty();
    for (const [id, group] of this.groups) {
      if (group.kind !== 'from-node') continue;
      const inside = new Set(this.descendants(id));
      const scope = new Set([id, ...inside]);
      const retained = group.suppressedEdgeIds.filter((edgeId) => {
        const edge = this.edges.get(edgeId);
        return (
          edge && scope.has(edge.data.source) && scope.has(edge.data.target)
        );
      });
      const pivotLinks = [...(this.adjacent.get(id) ?? [])].filter((edgeId) => {
        const edge = this.edges.get(edgeId)!;
        return inside.has(
          edge.data.source === id ? edge.data.target : edge.data.source,
        );
      });
      group.suppressedEdgeIds = [...new Set([...retained, ...pivotLinks])];
    }
  }
  reparent(id: string, parent?: string) {
    const node = this.nodes.get(id);
    if (
      !node ||
      parent === id ||
      node.data.parent === parent ||
      (parent &&
        (!this.groups.has(parent) || this.descendants(id).includes(parent)))
    )
      return false;
    if (parent) node.data.parent = parent;
    else delete node.data.parent;
    this.index();
    this.refreshSuppression();
    const target = parent ? this.groups.get(parent) : undefined;
    if (target?.kind === 'from-node') {
      const inside = new Set([parent!, ...this.descendants(parent!)]);
      const internalLinks = [...(this.adjacent.get(id) ?? [])].filter(
        (edgeId) => {
          const edge = this.edges.get(edgeId)!;
          return inside.has(edge.data.source) && inside.has(edge.data.target);
        },
      );
      target.suppressedEdgeIds = [
        ...new Set([...target.suppressedEdgeIds, ...internalLinks]),
      ];
    }
    return true;
  }
  canDrop(id: string, target?: string) {
    const node = this.nodes.get(id);
    if (!node || !this.dragAndDropEnabled) return false;
    return (
      !target ||
      (target !== id &&
        this.groups.has(target) &&
        !this.groups.get(target)?.collapsed &&
        !this.descendants(id).includes(target))
    );
  }
  group(ids: string[]) {
    const roots = this.roots(ids);
    if (roots.length < 2) return;
    const parents = new Set(roots.map((id) => this.nodes.get(id)!.data.parent));
    if (parents.size !== 1) return;
    const positions = roots.map(
      (id) => this.nodes.get(id)!.position ?? { x: 0, y: 0 },
    );
    const id = `group-${v4()}`;
    const parent = this.nodes.get(roots[0])!.data.parent;
    this.nodes.set(id, {
      data: { id, ...(parent ? { parent } : {}) },
      position: {
        x:
          (Math.min(...positions.map((p) => p.x)) +
            Math.max(...positions.map((p) => p.x))) /
          2,
        y:
          (Math.min(...positions.map((p) => p.y)) +
            Math.max(...positions.map((p) => p.y))) /
          2,
      },
    });
    this.groups.set(id, {
      kind: 'wrapper',
      collapsed: false,
      suppressedEdgeIds: [],
    });
    for (const child of roots) this.nodes.get(child)!.data.parent = id;
    this.index();
    return id;
  }
  ungroup(ids: string[]) {
    for (const id of ids) {
      const group = this.groups.get(id),
        node = this.nodes.get(id);
      if (!group || !node) continue;
      for (const child of this.children.get(id) ?? []) {
        if (node.data.parent)
          this.nodes.get(child)!.data.parent = node.data.parent;
        else delete this.nodes.get(child)!.data.parent;
      }
      this.groups.delete(id);
      this.index();
      if (group.kind === 'wrapper') this.removeNodes([id]);
    }
    this.index();
    this.refreshSuppression();
  }
  private conversionCandidates(id: string) {
    if (!this.nodes.has(id) || this.groups.has(id)) return [];
    const parent = this.nodes.get(id)!.data.parent;
    const descendants = new Set(this.descendants(id));
    const candidates = new Set<string>();
    for (const edgeId of this.adjacent.get(id) ?? []) {
      const edge = this.edges.get(edgeId)!;
      if (edge.data.source !== id || edge.data.target === id) continue;
      let root = edge.data.target;
      // Find a sibling in the pivot's scope, never its ancestor or a member of
      // another scope. Lifting to the graph root would create a hierarchy cycle.
      while (this.nodes.get(root)?.data.parent !== parent) {
        const next = this.nodes.get(root)?.data.parent;
        if (!next || next === id) break;
        root = next;
      }
      if (
        root !== id &&
        !descendants.has(root) &&
        this.nodes.get(root)?.data.parent === parent &&
        !this.descendants(root).includes(id)
      )
        candidates.add(root);
    }
    const scope = new Set([id, ...candidates]);
    return [...candidates].filter((nodeId) =>
      [...(this.adjacent.get(nodeId) ?? [])].every((edgeId) => {
        const edge = this.edges.get(edgeId)!;
        return scope.has(
          edge.data.source === nodeId ? edge.data.target : edge.data.source,
        );
      }),
    );
  }
  convertToGroup(id: string) {
    const candidates = this.conversionCandidates(id);
    if (!candidates.length) return;
    this.groups.set(id, {
      kind: 'from-node',
      collapsed: false,
      suppressedEdgeIds: [],
    });
    for (const child of candidates) this.reparent(child, id);
    this.index();
    this.refreshSuppression();
  }
  collapse(id: string, recursive = false) {
    this.setCollapsed(id, true, recursive);
  }
  expand(id: string, recursive = false) {
    this.setCollapsed(id, false, recursive);
  }
  private setCollapsed(id: string, collapsed: boolean, recursive: boolean) {
    this.dirty();
    for (const target of [id, ...(recursive ? this.descendants(id) : [])]) {
      const group = this.groups.get(target);
      if (group) group.collapsed = collapsed;
    }
  }
  addNodes(nodes: GraphNodeDefinition[]) {
    for (const node of nodes)
      if (!this.nodes.has(node.data.id))
        this.nodes.set(node.data.id, clone(node));
    this.index();
    for (const id of this.children.keys())
      if (!this.groups.has(id))
        this.groups.set(id, {
          kind: 'wrapper',
          collapsed: false,
          suppressedEdgeIds: [],
        });
  }
  updateNode(id: string, patch: GraphNodePatch) {
    this.dirty();
    const node = this.nodes.get(id);
    if (!node) return;
    if (patch.position)
      this.translate([id], {
        x: patch.position.x - (node.position?.x ?? 0),
        y: patch.position.y - (node.position?.y ?? 0),
      });
    if (patch.data?.parent !== undefined)
      this.reparent(id, patch.data.parent || undefined);
    const data = { ...patch.data };
    delete data.parent;
    delete data.id;
    Object.assign(node.data, data);
    if (patch.classes !== undefined) node.classes = clone(patch.classes);
    if (
      this.groups.has(id) &&
      typeof data.width === 'number' &&
      typeof data.height === 'number'
    )
      this.resizeGroup(id, { width: data.width, height: data.height });
  }
  removeNodes(ids: string[]) {
    const removed = new Set(ids.flatMap((id) => [id, ...this.descendants(id)]));
    for (const id of removed) {
      this.nodes.delete(id);
      this.groups.delete(id);
    }
    this.removeEdges(
      [...this.edges.values()]
        .filter(
          (edge) =>
            removed.has(edge.data.source) || removed.has(edge.data.target),
        )
        .map((edge) => edge.data.id),
    );
    this.index();
    this.refreshSuppression();
  }
  addEdges(edges: GraphEdgeDefinition[]) {
    for (const edge of edges)
      if (
        !this.edges.has(edge.data.id) &&
        this.nodes.has(edge.data.source) &&
        this.nodes.has(edge.data.target)
      )
        this.edges.set(edge.data.id, clone(edge));
    this.index();
    this.refreshSuppression();
  }
  updateEdge(id: string, patch: GraphEdgePatch) {
    const edge = this.edges.get(id);
    if (!edge) return;
    const data = { ...edge.data, ...patch.data, id };
    if (!this.nodes.has(data.source) || !this.nodes.has(data.target)) return;
    edge.data = data;
    if (patch.classes !== undefined) edge.classes = clone(patch.classes);
    this.index();
    this.refreshSuppression();
  }
  removeEdges(ids: string[]) {
    const removed = new Set(ids.flatMap((id) => this.edgeMembers(id)));
    for (const id of removed) this.edges.delete(id);
    let changed = true;
    while (changed) {
      changed = false;
      for (const [id, bundle] of this.bundles) {
        bundle.memberIds = bundle.memberIds.filter(
          (member) => this.edges.has(member) || this.bundles.has(member),
        );
        if (!bundle.memberIds.length) {
          this.bundles.delete(id);
          changed = true;
        }
      }
    }
    this.index();
    this.refreshSuppression();
  }
  edgeMembers(id: string): string[] {
    const bundle = this.bundles.get(id);
    return bundle
      ? bundle.memberIds.flatMap((member) => this.edgeMembers(member))
      : this.edges.has(id)
        ? [id]
        : [];
  }
  canCollapseEdges(ids: string[]) {
    const edges =
      this.projection().edges?.filter((edge) => ids.includes(edge.data.id)) ??
      [];
    return (
      edges.length >= 2 &&
      edges.every(
        (edge) =>
          edge.selectable !== false &&
          !['virtual', 'bulk-link-preview'].includes(edge.data.type),
      ) &&
      edges.every(
        (edge) => !edge.data.bundleId || edge.data.bundleId === edge.data.id,
      ) &&
      new Set(edges.map((edge) => pair(edge.data.source, edge.data.target)))
        .size === 1
    );
  }
  collapseEdges(ids: string[]) {
    this.dirty();
    if (!this.canCollapseEdges(ids)) return;
    const visible = this.projection().edges!.filter((edge) =>
      ids.includes(edge.data.id),
    );
    const memberIds = [
      ...new Set<string>(
        visible.flatMap((edge) =>
          this.bundles.has(edge.data.id)
            ? [edge.data.id]
            : (edge.data.memberIds ?? [edge.data.id]),
        ),
      ),
    ];
    const id = `cedge-${v4()}`;
    const dominantLink = computeDominantLinkType(
      memberIds
        .flatMap((member) => this.edgeMembers(member))
        .map((member) => this.edges.get(member)!),
    ).dominantType;
    this.bundles.set(id, { memberIds, dominantLink });
    this.dirty();
    return id;
  }
  expandEdges(ids: string[], recursive = false) {
    this.dirty();
    const visible = this.projection().edges ?? [];
    for (const id of ids) {
      const target =
        visible.find((edge) => edge.data.id === id)?.data.bundleId ?? id;
      const pending = [target];
      while (pending.length) {
        const next = pending.pop()!,
          bundle = this.bundles.get(next);
        if (!bundle) continue;
        if (recursive) pending.push(...bundle.memberIds);
        for (const [parentId, parent] of this.bundles) {
          if (parentId !== next && parent.memberIds.includes(next))
            parent.memberIds = parent.memberIds.flatMap((member) =>
              member === next ? bundle.memberIds : [member],
            );
        }
        this.bundles.delete(next);
      }
    }
    this.dirty();
  }
  connectedEdges(id: string) {
    return this.projection()
      .edges!.filter(
        (edge) => edge.data.source === id || edge.data.target === id,
      )
      .map((edge) => edge.data.id);
  }
  removeUnavailable(ids?: string[]) {
    for (const id of ids ?? [...this.nodes.keys()]) {
      const node = this.nodes.get(id);
      if (!hasGraphClass(node?.classes, UNAVAILABLE_CLASS)) continue;
      if (this.groups.has(id)) this.ungroup([id]);
      this.removeNodes([id]);
    }
  }
  getNodeState(id: string) {
    const group = this.groups.get(id);
    const connected = this.connectedEdges(id);
    return {
      isGroup: !!group,
      isCollapsed: !!group?.collapsed,
      isGroupFromNode: group?.kind === 'from-node',
      hasChildren: !!this.children.get(id)?.size,
      canConvert:
        this.nodes.has(id) && this.conversionCandidates(id).length > 0,
      canCollapseEdges: this.canCollapseEdges(connected),
      canExpandEdges: this.projection().edges!.some(
        (edge) =>
          connected.includes(edge.data.id) && edge.data.type === 'compound',
      ),
    };
  }
  frames(): GroupFrame[] {
    return (this.framesCache ??= getCompoundFrames(this, (id) =>
      this.descendants(id),
    ));
  }
  projection(): GraphElements {
    return (this.projectionCache ??= projectCompoundGraph(this, (id) =>
      this.edgeMembers(id),
    ));
  }
}
